/**
 * LedgerLine — Tier 0 slice-local primitive (epic SCRUM-332 / SCRUM-333).
 *
 * One row of the reservation cash ledger: a label on the left, a money
 * value on the right, with optional muted note, bold weight (subtotals /
 * totals), and a brand accent (highlighted line). Negative values (e.g.
 * an OTA commission line `−$120`) render in the danger token.
 *
 * **Design source.** `design_handoff_reservations/LEDGER_MODEL.md` +
 * `source/reservations.jsx` ledger rows — VISUAL reference only (the
 * bundle's `Reservation`/`RES_TYPES` model is STALE). The ledger's
 * Channel-Collect vs Property-Collect shape is a LATER full-stack ticket;
 * this primitive only formats a single (label, cents) row.
 *
 * **Money is `Cents`.** (CLAUDE.md rule 2 / FE rule 6.) The value is a
 * `Cents`-integer count. It is formatted for DISPLAY via `formatCents`
 * (`apps/admin/src/shared/formatters/entity-change/values.ts` — Intl-based,
 * tested: 12345→"$123.45", 0→"$0.00", 5→"$0.05", fail-closed to a neutral
 * marker for non-finite) through `useIntl()`. NO hand-rolled `/100`, NO
 * bare arithmetic on money. `Intl` supplies the leading "−" for negatives;
 * we only inspect the sign to pick the color token.
 *
 * **Tokens (ADR frontend-0003, SCRUM-78/79).** Colors come from semantic
 * tokens only: negative → `--danger-500`, `accent` → `--brand-600`,
 * otherwise `--fg-primary`; note is `--fg-tertiary`. No hex, no bare
 * inline numerics.
 *
 * **Tier (ADR frontend-0004).** Tier 0: slice-local, NOT barrel-exported.
 * Promote on the second-consumer trigger (a second slice rendering ledger
 * rows).
 */

import * as React from 'react';
import { useIntl } from 'react-intl';
import { formatCents } from '../../../shared/formatters/entity-change/values.js';

export interface LedgerLineProps {
  /** Row label (e.g. "Cleaning fee", "VRBO commission (API)"). */
  readonly label: string;
  /** Money value in `Cents` (integer). Negatives are credits/liabilities. */
  readonly valueCents: number;
  /** Optional muted sub-note under the label. */
  readonly note?: string;
  /** Bold weight for subtotal / total lines. */
  readonly bold?: boolean;
  /** Brand-accent color for a highlighted line. */
  readonly accent?: boolean;
}

export function LedgerLine({
  label,
  valueCents,
  note,
  bold = false,
  accent = false,
}: LedgerLineProps): React.ReactElement {
  const intl = useIntl();
  // Display-boundary conversion only — never re-computed for domain use.
  const formatted = formatCents(valueCents, intl);

  // Sign inspection is a comparison, not money arithmetic — safe. Negative
  // values are painted danger regardless of `accent`/`bold` (a credit stays
  // legible as a reduction). Only finite negatives count; a non-finite
  // input already renders as the neutral marker.
  const isNegative = Number.isFinite(valueCents) && valueCents < 0;

  const valueColorClass = isNegative
    ? 'text-[color:var(--danger-500)]'
    : accent
      ? 'text-[color:var(--brand-600)]'
      : 'text-[color:var(--fg-primary)]';

  const weightClass = bold ? 'font-semibold' : 'font-normal';

  return (
    // Three sibling cells on one baseline-aligned flex row (design
    // reservations.jsx:322-328). The label is a fixed, non-shrinking left cell
    // (~180px ≈ w-44); the note is its OWN middle cell (flex-1, present even
    // when empty so the value stays right-aligned); the value pins to the hard
    // right edge (shrink-0 + text-right). Note and value are DIRECT siblings —
    // the note is never nested inside the label wrapper.
    <div className="flex items-baseline gap-4 py-1">
      <span
        className={`w-44 shrink-0 text-sm ${bold ? 'font-semibold' : 'font-normal'} text-[color:var(--fg-primary)]`}
      >
        {label}
      </span>
      {note !== undefined ? (
        <span className="min-w-0 flex-1 truncate text-xs text-[color:var(--fg-tertiary)]">
          {note}
        </span>
      ) : (
        <span className="flex-1" aria-hidden="true" />
      )}
      <span
        className={`shrink-0 text-right text-sm tabular-nums ${weightClass} ${valueColorClass}`}
      >
        {formatted}
      </span>
    </div>
  );
}
